import { useEffect, useRef, useState } from 'react';
import FireflyMeadow from './FireflyMeadow';
import MorningMeadow, { type SunTransition } from './MorningMeadow';

// Home background (v2): the same meadow by night and by day.
// Switching the theme plays a sunrise (night fades out as the sun comes up)
// or a sunset (the sun goes down, then the night fades in).

export default function MeadowBackground({ isNight, className = '' }: { isNight: boolean; className?: string }) {
  const [showDay, setShowDay] = useState(!isNight);
  const [showNight, setShowNight] = useState(isNight);
  const [nightOpacity, setNightOpacity] = useState(isNight ? 1 : 0);
  const [sun, setSun] = useState<SunTransition>(null);
  const [prev, setPrev] = useState(isNight);
  const mounted = useRef(false);

  // When the theme flips, show both scenes and start the sun moving.
  if (prev !== isNight) {
    setPrev(isNight);
    setShowDay(true);
    setShowNight(true);
    setSun(isNight ? 'sunset' : 'sunrise');
    setNightOpacity(isNight ? 0 : 1);
  }

  // Then fade the night in or out, and drop the scene that is no longer seen.
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return; }
    const timers: number[] = [];
    const later = (fn: () => void, ms: number) => timers.push(window.setTimeout(fn, ms));
    if (!isNight) {
      later(() => setNightOpacity(0), 50);
      later(() => setShowNight(false), 2200);
    } else {
      later(() => setNightOpacity(1), 1000);
      later(() => { setShowDay(false); setSun(null); }, 3200);
    }
    return () => timers.forEach(clearTimeout);
  }, [isNight]);

  return (
    <div aria-hidden="true" className={`pointer-events-none ${className}`}>
      {showDay && <MorningMeadow className="absolute inset-0" transition={sun} />}
      {showNight && (
        <div className="absolute inset-0 transition-opacity duration-[1800ms] ease-in-out" style={{ opacity: nightOpacity }}>
          <FireflyMeadow className="absolute inset-0" />
        </div>
      )}
    </div>
  );
}
